import { fetchHelpCenterBrandingAction } from 'epics/model/help-center';
import { store } from 'providers';
import { createResource } from 'react-resource-router';
import { getRequest } from 'relay-runtime';
import type { State } from 'state';
import BrandingQuery from '@atlassian/help-center-common-util/graphql-relay-connector/help-center-branding/__generated__/helpCenterBrandingQuery.graphql';

/**
 * The maximum time (in ms) to wait for branding data to populate in the Redux store
 * before resolving the promise anyway. This prevents hanging indefinitely if the epic
 * errors silently. SSR has a hardcoded timeout of 4s, so we use 3s to resolve
 * gracefully before that limit is hit.
 */
export const BRANDING_DATA_TIMEOUT_MS = 3000;

/**
 * Returns true when the branding data fetch has completed — either successfully or with an error.
 * The `isBrandingLoaded` flag is set by `updateHCBrandingLoaderAction(true)` which is dispatched
 * on both success and error paths in the epic.
 */
const isBrandingResolved = (state: State): boolean => {
    return state.persisted.helpCenter.isBrandingLoaded === true;
};

/**
 * Returns a promise that resolves once the Redux store's help center branding fetch
 * has completed (isBrandingLoaded === true), or resolves null on timeout.
 *
 * This bridges the gap between the relay resource world (which needs a promise to
 * await before rendering) and the Redux epic world (which populates the store
 * asynchronously via epic side effects).
 */
export const waitForBrandingData = (): Promise<null> => {
    return new Promise<null>((resolve) => {
        // Check if branding data is already in the store (e.g. from initialModel/SSR bootstrap)
        if (isBrandingResolved(store.getState() as State)) {
            resolve(null);
            return;
        }

        // Safety timeout: resolve even if the epic silently fails, so we don't block SSR forever.
        // We capture the unsubscribe function reference via a mutable object so we can use it in the timeout.
        const subscription: { unsubscribe?: () => void } = {};

        const timeoutId = setTimeout(() => {
            subscription.unsubscribe?.();
            resolve(null);
        }, BRANDING_DATA_TIMEOUT_MS);

        subscription.unsubscribe = store.subscribe(() => {
            const state = store.getState() as State;
            if (isBrandingResolved(state)) {
                clearTimeout(timeoutId);
                subscription.unsubscribe?.();
                resolve(null);
            }
        });
    });
};

/**
 * A resource that dispatches the branding fetch action and waits for the Redux store
 * to be populated before resolving. This fixes an SSR race condition where the page
 * would render before branding data was available, causing a server/client mismatch
 * during rehydration.
 *
 * This is a replacement for `brandingResource` from the multi-help-centers resources
 * that properly bridges the relay and redux worlds for the login route.
 */
export const waitingBrandingResource = createResource({
    type: 'RELAY_HELP_CENTER_BRANDING',
    getKey: (_, { cloudId, helpCenterAri }: { cloudId: string; helpCenterAri: string }) => {
        const request = getRequest(BrandingQuery);
        const params = request.params;
        return `${String(params.id)}${JSON.stringify({
            cloudId,
            helpCenterAri,
        })}`;
    },
    getData: () => {
        store.dispatch(fetchHelpCenterBrandingAction());
        return waitForBrandingData();
    },
});
